import { config } from '../config';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private reunionId: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  setReunionId(id: string | null) {
    this.reunionId = id;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    let url = `${this.baseUrl}${path}`;
    if (params) {
      url += '?' + new URLSearchParams(params).toString();
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((fetchOptions.headers as Record<string, string>) || {}),
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    if (this.reunionId) {
      headers['X-Reunion-Id'] = this.reunionId;
    }

    const response = await fetch(url, { ...fetchOptions, headers });

    if (!response.ok) {
      // Handle 401 globally: clear auth and redirect to login
      if (response.status === 401) {
        // Dynamic import to avoid circular dependency with authStore
        const { useAuthStore } = await import('../store/authStore');
        const { clearAuth } = useAuthStore.getState();
        clearAuth();

        // Only redirect if not already on an auth page
        if (
          !window.location.pathname.startsWith('/login') &&
          !window.location.pathname.startsWith('/register')
        ) {
          window.location.href = '/login';
        }
      }

      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new ApiError(
        response.status,
        error.error?.message || error.message || 'Request failed',
        error.error?.code,
      );
    }

    return response.json();
  }

  get<T>(path: string, params?: Record<string, string>) {
    return this.request<T>(path, { method: 'GET', params });
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: 'POST', body: JSON.stringify(body) });
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
  }

  delete<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  }
}

export const api = new ApiClient(config.apiBaseUrl);
